import { useGetUserQuery } from '@/api/piloteer';
import { isClientVersionErrorCode } from '@/api/piloteer/base';
import { useAuthSetup } from '@/services/auth';
import { useAppSelector } from '@/store/hooks';
import { UserRole } from '@/types/user';
import { APP_ROUTES } from '@/ui/app-routes';
import { lazyWithChunkRecovery } from '@/utils/chunk-load';
import { FetchBaseQueryError } from '@reduxjs/toolkit/query';
import * as Sentry from '@sentry/react';
import { Suspense } from 'react';
import {
  Navigate,
  Route,
  Routes,
  useLocation,
  useSearchParams,
} from 'react-router-dom';

import { FullPageError } from '../@components/feedback/error/full-page-error';
import { FullPageLoading } from '../@components/feedback/loading/full-page-loading';
import { EnforceAuth } from '../@components/utility/enforce-auth';
import {
  PerformancePartnerRoute,
  RoleBasedRoute,
} from '../@components/utility/role-based-route';
import { SuperAdminRoute } from '../@components/utility/super-admin-route';
import NotFound from '../pages/not-found';
import GoogleCalendarOAuthCallback from '../pages/oauth-callback/google';
import MicrosoftCalendarOAuthCallback from '../pages/oauth-callback/microsoft/microsoft';
import { SettingsLoading } from '../pages/settings/loading';

export { APP_ROUTES };

const sentryDsn = import.meta.env.VITE_SENTRY_DSN as string;
const SentryRoutes = sentryDsn
  ? Sentry.withSentryReactRouterV6Routing(Routes)
  : Routes;

const CoachPage = lazyWithChunkRecovery(() => import('@/ui/pages/coach'));
const CoachMember = lazyWithChunkRecovery(
  () => import('@/ui/pages/coach/member-dashboard')
);
const CoachCompany = lazyWithChunkRecovery(
  () => import('@/ui/pages/coach/company-dashboard')
);
const CoachingSettings = lazyWithChunkRecovery(
  () => import('@/ui/pages/settings/coaching')
);
const AdminCoaches = lazyWithChunkRecovery(
  () => import('@/ui/pages/super-admin/coaches/coaches-page')
);
const ForgotPassword = lazyWithChunkRecovery(
  () => import('@/ui/pages/auth/forgot-password')
);
const Login = lazyWithChunkRecovery(() => import('@/ui/pages/auth/login'));
const ResetPassword = lazyWithChunkRecovery(
  () => import('@/ui/pages/auth/reset-password/')
);
// const Signup = lazy(() => import('@/ui/pages/auth/signup')); // Manual signup disabled
const VerifyEmail = lazyWithChunkRecovery(
  () => import('@/ui/pages/auth/verify-email')
);
const Dashboard = lazyWithChunkRecovery(() => import('@/ui/pages/dashboard'));
const DashboardV2 = lazyWithChunkRecovery(
  () => import('@/ui/pages/dashboard-v2')
);
const InvalidClient = lazyWithChunkRecovery(
  () => import('@/ui/pages/invalid-client')
);
const Reports = lazyWithChunkRecovery(() => import('@/ui/pages/reports'));
const MeetingReport = lazyWithChunkRecovery(
  () => import('@/ui/pages/reports/meeting')
);
const PublicMeetingReport = lazyWithChunkRecovery(
  () => import('@/ui/pages/reports/meeting/public-meeting')
);
const SessionReport = lazyWithChunkRecovery(
  () => import('@/ui/pages/reports/session')
);
const PublicSessionReport = lazyWithChunkRecovery(
  () => import('@/ui/pages/reports/session/public-session')
);
const Billing = lazyWithChunkRecovery(
  () => import('@/ui/pages/settings/billing')
);
const ProfileSettingsPage = lazyWithChunkRecovery(
  () => import('@/ui/pages/settings/profile')
);
const KnowledgeBaseSettingsPage = lazyWithChunkRecovery(
  () => import('@/ui/pages/settings/knowledge-base')
);
const LanguageSettingsPage = lazyWithChunkRecovery(
  () => import('@/ui/pages/settings/language')
);
const CompanyControlsPage = lazyWithChunkRecovery(
  () => import('@/ui/pages/settings/company-controls')
);
const IntegrationsPage = lazyWithChunkRecovery(
  () => import('@/ui/pages/settings/integrations')
);
const SecuritySettingsPage = lazyWithChunkRecovery(
  () => import('@/ui/pages/settings/security')
);
const CommanderSettingsPage = lazyWithChunkRecovery(
  () => import('@/ui/pages/settings/commander')
);
const ZoomOAuthCallback = lazyWithChunkRecovery(
  () => import('@/ui/pages/oauth-callback/zoom/zoom')
);
const SetPasswordPage = lazyWithChunkRecovery(
  () => import('@/ui/pages/auth/set-password')
);

// Multi-tenant pages
const PartnerDashboard = lazyWithChunkRecovery(
  () => import('@/ui/pages/partner/dashboard/partner-dashboard')
);
const PartnerCompanyDetail = lazyWithChunkRecovery(
  () => import('@/ui/pages/partner/company/index')
);

const PartnerSettings = lazyWithChunkRecovery(
  () => import('@/ui/pages/partner/settings/partner-settings')
);
const PartnerCompanyAnalytics = lazyWithChunkRecovery(
  () => import('@/ui/pages/analytics/partner-company-analytics')
);
const CompanyTeams = lazyWithChunkRecovery(
  () => import('@/ui/pages/company/teams/company-teams')
);
const CompanyTeamDetail = lazyWithChunkRecovery(
  () => import('@/ui/pages/company/teams/team-detail')
);
const CompanyUsers = lazyWithChunkRecovery(
  () => import('@/ui/pages/company/users/company-users')
);
const CompanySettings = lazyWithChunkRecovery(
  () => import('@/ui/pages/company/settings/company-settings')
);
const TeamDashboard = lazyWithChunkRecovery(
  () => import('@/ui/pages/team/dashboard/team-dashboard')
);

// Analytics pages
const Analytics = lazyWithChunkRecovery(() => import('@/ui/pages/analytics'));
const PartnerAnalytics = lazyWithChunkRecovery(
  () => import('@/ui/pages/analytics/partner-analytics')
);
const TeamAnalytics = lazyWithChunkRecovery(
  () => import('@/ui/pages/analytics/team-analytics')
);
const MyAnalytics = lazyWithChunkRecovery(
  () => import('@/ui/pages/analytics/my-analytics')
);

// Admin pages
const SuperAdminLayout = lazyWithChunkRecovery(() =>
  import('@/ui/pages/super-admin/layout/super-admin-layout').then((m) => ({
    default: m.SuperAdminLayout,
  }))
);
const SuperAdminOverview = lazyWithChunkRecovery(
  () => import('@/ui/pages/super-admin/overview/super-admin-overview')
);
const SuperAdminPartners = lazyWithChunkRecovery(
  () => import('@/ui/pages/super-admin/partners/partners-page')
);
const SuperAdminCompanies = lazyWithChunkRecovery(
  () => import('@/ui/pages/super-admin/companies/companies-page')
);
const SuperAdminUsers = lazyWithChunkRecovery(
  () => import('@/ui/pages/super-admin/users/users-page')
);
const SuperAdminAppConfig = lazyWithChunkRecovery(
  () => import('@/ui/pages/super-admin/app-config/app-config-page')
);
const SuperAdminClients = lazyWithChunkRecovery(
  () => import('@/ui/pages/super-admin/clients/clients-page')
);
const SuperAdminOperations = lazyWithChunkRecovery(
  () => import('@/ui/pages/super-admin/operations/operations-page')
);
const OnboardCompany = lazyWithChunkRecovery(
  () => import('@/ui/pages/partner/onboard-company')
);

// Wrapper component to handle public vs private session routes based on query parameter
const SessionReportWrapper = () => {
  const [searchParams] = useSearchParams();
  const isPublic = searchParams.get('public') === 'true';

  if (isPublic) {
    return <PublicSessionReport />;
  }

  return (
    <EnforceAuth>
      <SessionReport />
    </EnforceAuth>
  );
};

// Wrapper component to handle public vs private meeting routes based on query parameter
const MeetingReportWrapper = () => {
  const [searchParams] = useSearchParams();
  const isPublic = searchParams.get('public') === 'true';

  if (isPublic) {
    return <PublicMeetingReport />;
  }

  return (
    <EnforceAuth>
      <MeetingReport />
    </EnforceAuth>
  );
};

// Helper function to get the appropriate dashboard URL based on user role
const getDashboardUrl = (userRole?: UserRole) => {
  switch (userRole) {
    case UserRole.CHANNEL_PARTNER_ADMIN:
      return APP_ROUTES.PARTNER.DASHBOARD;
    case UserRole.COMPANY_ADMIN:
      return APP_ROUTES.DASHBOARD; // Company admins use the regular dashboard
    case UserRole.COMPANY_MANAGER:
      return APP_ROUTES.TEAM.DASHBOARD;
    case UserRole.COMPANY_MEMBER:
      return APP_ROUTES.DASHBOARD;
    default:
      return APP_ROUTES.DASHBOARD;
  }
};

const ApplicationRoutes = () => {
  useAuthSetup();
  const {
    session,
    isLoading: authLoading,
    error: authError,
  } = useAppSelector((state) => state.auth);

  // Get user data from backend API
  const {
    data: backendUser,
    isLoading: userLoading,
    error: userError,
  } = useGetUserQuery(undefined, {
    skip: !session, // Only fetch when we have a session
  });

  const pathname = useLocation().pathname;
  const searchParams = new URLSearchParams(window.location.search);
  const isOnInvalidClientRoute = pathname === APP_ROUTES.INVALID_CLIENT;
  const isOnPublicRoute =
    (pathname.startsWith('/sessions/') || pathname.startsWith('/meetings/')) &&
    searchParams.get('public') === 'true';
  const userErrorCode = (userError as FetchBaseQueryError | undefined)?.data as
    | { error_code?: number | string }
    | undefined;

  if (
    !isOnInvalidClientRoute &&
    isClientVersionErrorCode(userErrorCode?.error_code)
  ) {
    return <Navigate to={APP_ROUTES.INVALID_CLIENT} replace />;
  }

  // Show loading if auth is loading, or if we have a session but backend user is still loading
  if (
    !isOnInvalidClientRoute &&
    !isOnPublicRoute &&
    (authLoading || (session && userLoading))
  ) {
    return <FullPageLoading />;
  }

  // Show error if auth error or user fetch error (but not for public routes)
  if (!isOnInvalidClientRoute && !isOnPublicRoute && (authError || userError)) {
    return <FullPageError />;
  }

  // Determine redirect URL based on backend user role
  let dashboardUrl = APP_ROUTES.AUTH.MAIN;
  if (session) {
    if (backendUser?.role) {
      dashboardUrl = getDashboardUrl(backendUser.role);
    } else {
      // Fallback to default dashboard if role is not available yet
      dashboardUrl = APP_ROUTES.DASHBOARD;
    }
  }

  return (
    <SentryRoutes>
      {[
        APP_ROUTES.COACH.MAIN,
        APP_ROUTES.COACH.MEMBER,
        APP_ROUTES.COACH.COMPANY,
        APP_ROUTES.COACH.TEAM,
      ].map((path) => (
        <Route
          key={path}
          path={path}
          element={
            <Suspense fallback={<FullPageLoading />}>
              <PerformancePartnerRoute>
                {path === APP_ROUTES.COACH.MAIN ? (
                  <CoachPage />
                ) : path === APP_ROUTES.COACH.MEMBER ? (
                  <CoachMember />
                ) : (
                  <CoachCompany />
                )}
              </PerformancePartnerRoute>
            </Suspense>
          }
        />
      ))}
      <Route
        path={APP_ROUTES.SETTINGS.COACHING}
        element={
          <Suspense fallback={<SettingsLoading />}>
            <RoleBasedRoute
              allowedRoles={[
                UserRole.COMPANY_ADMIN,
                UserRole.COMPANY_MANAGER,
                UserRole.COMPANY_MEMBER,
              ]}
            >
              <CoachingSettings />
            </RoleBasedRoute>
          </Suspense>
        }
      />
      <Route
        path={APP_ROUTES.ROOT}
        element={<Navigate to={dashboardUrl} replace />}
      />

      <Route
        path={APP_ROUTES.DASHBOARD}
        element={
          <Suspense fallback={<FullPageLoading />}>
            <EnforceAuth>
              <DashboardV2 />
            </EnforceAuth>
          </Suspense>
        }
      />
      <Route
        path={APP_ROUTES.DASHBOARD_CLASSIC}
        element={
          <Suspense fallback={<FullPageLoading />}>
            <EnforceAuth>
              <Dashboard />
            </EnforceAuth>
          </Suspense>
        }
      />
      <Route
        path={APP_ROUTES.DASHBOARD_V2}
        element={
          <Suspense fallback={<FullPageLoading />}>
            <EnforceAuth>
              <DashboardV2 />
            </EnforceAuth>
          </Suspense>
        }
      />

      <Route
        path={APP_ROUTES.SETTINGS.MAIN}
        element={<Navigate to={APP_ROUTES.SETTINGS.PROFILE} />}
      />
      <Route
        path={APP_ROUTES.SETTINGS.PROFILE}
        element={
          <Suspense fallback={<SettingsLoading />}>
            <EnforceAuth>
              <ProfileSettingsPage />
            </EnforceAuth>
          </Suspense>
        }
      />
      <Route
        path={APP_ROUTES.SETTINGS.KNOWLEDGE_BASE}
        element={
          <Suspense fallback={<SettingsLoading />}>
            <RoleBasedRoute allowedRoles={[UserRole.COMPANY_ADMIN]}>
              <KnowledgeBaseSettingsPage />
            </RoleBasedRoute>
          </Suspense>
        }
      />
      <Route
        path={APP_ROUTES.SETTINGS.LANGUAGE}
        element={
          <Suspense fallback={<SettingsLoading />}>
            <RoleBasedRoute allowedRoles={[UserRole.COMPANY_ADMIN]}>
              <LanguageSettingsPage />
            </RoleBasedRoute>
          </Suspense>
        }
      />
      <Route
        path={APP_ROUTES.SETTINGS.COMPANY_CONTROLS}
        element={
          <Suspense fallback={<SettingsLoading />}>
            <RoleBasedRoute allowedRoles={[UserRole.COMPANY_ADMIN]}>
              <CompanyControlsPage />
            </RoleBasedRoute>
          </Suspense>
        }
      />
      <Route
        path='/settings/disclosure'
        element={<Navigate replace to={APP_ROUTES.SETTINGS.COMPANY_CONTROLS} />}
      />
      <Route
        path={APP_ROUTES.SETTINGS.BILLING}
        element={
          <Suspense fallback={<SettingsLoading />}>
            <EnforceAuth>
              <Billing />
            </EnforceAuth>
          </Suspense>
        }
      />
      <Route
        path={APP_ROUTES.SETTINGS.INTEGRATIONS}
        element={
          <Suspense fallback={<SettingsLoading />}>
            <EnforceAuth>
              <IntegrationsPage />
            </EnforceAuth>
          </Suspense>
        }
      />
      <Route
        path={APP_ROUTES.SETTINGS.SECURITY}
        element={
          <Suspense fallback={<SettingsLoading />}>
            <EnforceAuth>
              <SecuritySettingsPage />
            </EnforceAuth>
          </Suspense>
        }
      />
      <Route
        path={APP_ROUTES.SETTINGS.COMMANDER}
        element={
          <Suspense fallback={<SettingsLoading />}>
            <EnforceAuth>
              <CommanderSettingsPage />
            </EnforceAuth>
          </Suspense>
        }
      />

      {/* Auth */}
      <Route
        path={APP_ROUTES.AUTH.MAIN}
        element={<Navigate to={APP_ROUTES.AUTH.LOGIN} replace />}
      />
      {/* Manual signup disabled - redirect to login */}
      <Route
        path={APP_ROUTES.AUTH.SIGNUP}
        element={<Navigate to={APP_ROUTES.AUTH.LOGIN} replace />}
      />
      <Route
        path={APP_ROUTES.AUTH.LOGIN}
        element={
          <Suspense fallback={<FullPageLoading />}>
            <Login />
          </Suspense>
        }
      />

      <Route
        path={APP_ROUTES.AUTH.FORGOT_PASSWORD}
        element={
          <Suspense fallback={<FullPageLoading />}>
            <ForgotPassword />
          </Suspense>
        }
      />
      <Route
        path={APP_ROUTES.AUTH.RESET_PASSWORD}
        element={
          <Suspense fallback={<FullPageLoading />}>
            <ResetPassword />
          </Suspense>
        }
      />
      <Route
        path={APP_ROUTES.AUTH.VERIFY_EMAIL}
        element={
          <Suspense fallback={<FullPageLoading />}>
            <VerifyEmail />
          </Suspense>
        }
      />

      <Route
        path={APP_ROUTES.AUTH.SET_PASSWORD}
        element={
          <Suspense fallback={<FullPageLoading />}>
            <SetPasswordPage />
          </Suspense>
        }
      />

      {/* Multi-tenant Routes */}
      {/* Channel Partner Routes */}
      <Route
        path={APP_ROUTES.PARTNER.DASHBOARD}
        element={
          <Suspense fallback={<FullPageLoading />}>
            <RoleBasedRoute allowedRoles={[UserRole.CHANNEL_PARTNER_ADMIN]}>
              <PartnerDashboard />
            </RoleBasedRoute>
          </Suspense>
        }
      />

      <Route
        path={APP_ROUTES.PARTNER.COMPANY_DETAIL}
        element={
          <Suspense fallback={<FullPageLoading />}>
            <RoleBasedRoute allowedRoles={[UserRole.CHANNEL_PARTNER_ADMIN]}>
              <PartnerCompanyDetail />
            </RoleBasedRoute>
          </Suspense>
        }
      />
      <Route
        path={APP_ROUTES.PARTNER.ONBOARD_COMPANY}
        element={
          <Suspense fallback={<FullPageLoading />}>
            <RoleBasedRoute allowedRoles={[UserRole.CHANNEL_PARTNER_ADMIN]}>
              <OnboardCompany />
            </RoleBasedRoute>
          </Suspense>
        }
      />
      <Route
        path={APP_ROUTES.PARTNER.SETTINGS}
        element={
          <Suspense fallback={<FullPageLoading />}>
            <RoleBasedRoute allowedRoles={[UserRole.CHANNEL_PARTNER_ADMIN]}>
              <PartnerSettings />
            </RoleBasedRoute>
          </Suspense>
        }
      />

      {/* Company Admin Routes */}
      <Route
        path={APP_ROUTES.COMPANY.TEAMS}
        element={
          <Suspense fallback={<FullPageLoading />}>
            <RoleBasedRoute allowedRoles={[UserRole.COMPANY_ADMIN]}>
              <CompanyTeams />
            </RoleBasedRoute>
          </Suspense>
        }
      />
      <Route
        path={APP_ROUTES.COMPANY.TEAM_DETAIL}
        element={
          <Suspense fallback={<FullPageLoading />}>
            <RoleBasedRoute allowedRoles={[UserRole.COMPANY_ADMIN]}>
              <CompanyTeamDetail />
            </RoleBasedRoute>
          </Suspense>
        }
      />
      <Route
        path={APP_ROUTES.COMPANY.USERS}
        element={
          <Suspense fallback={<FullPageLoading />}>
            <RoleBasedRoute allowedRoles={[UserRole.COMPANY_ADMIN]}>
              <CompanyUsers />
            </RoleBasedRoute>
          </Suspense>
        }
      />
      <Route
        path={APP_ROUTES.COMPANY.KNOWLEDGE_BASE}
        element={
          <Suspense fallback={<FullPageLoading />}>
            <RoleBasedRoute allowedRoles={[UserRole.COMPANY_ADMIN]}>
              <CompanySettings />
            </RoleBasedRoute>
          </Suspense>
        }
      />
      <Route
        path={APP_ROUTES.COMPANY.SETTINGS}
        element={
          <Suspense fallback={<FullPageLoading />}>
            <RoleBasedRoute allowedRoles={[UserRole.COMPANY_ADMIN]}>
              <CompanySettings />
            </RoleBasedRoute>
          </Suspense>
        }
      />
      <Route
        path={APP_ROUTES.COMPANY.LANGUAGE_SETTINGS}
        element={
          <Suspense fallback={<FullPageLoading />}>
            <RoleBasedRoute allowedRoles={[UserRole.COMPANY_ADMIN]}>
              <CompanySettings />
            </RoleBasedRoute>
          </Suspense>
        }
      />

      {/* Team Manager Routes */}
      <Route
        path={APP_ROUTES.TEAM.DASHBOARD}
        element={
          <Suspense fallback={<FullPageLoading />}>
            <RoleBasedRoute
              allowedRoles={[UserRole.COMPANY_ADMIN, UserRole.COMPANY_MANAGER]}
            >
              <TeamDashboard />
            </RoleBasedRoute>
          </Suspense>
        }
      />
      <Route
        path={APP_ROUTES.TEAM.DETAIL}
        element={
          <Suspense fallback={<FullPageLoading />}>
            <RoleBasedRoute
              allowedRoles={[UserRole.COMPANY_ADMIN, UserRole.COMPANY_MANAGER]}
            >
              <TeamDashboard />
            </RoleBasedRoute>
          </Suspense>
        }
      />

      {/* Super Admin Routes */}
      <Route
        path={APP_ROUTES.SUPER_ADMIN.DASHBOARD}
        element={
          <SuperAdminRoute>
            <Suspense fallback={<FullPageLoading />}>
              <SuperAdminLayout />
            </Suspense>
          </SuperAdminRoute>
        }
      >
        <Route
          path={APP_ROUTES.SUPER_ADMIN.COACHES}
          element={
            <Suspense fallback={<FullPageLoading />}>
              <AdminCoaches />
            </Suspense>
          }
        />
        <Route
          index
          element={
            <Suspense fallback={<FullPageLoading />}>
              <SuperAdminOverview />
            </Suspense>
          }
        />
        <Route
          path={APP_ROUTES.SUPER_ADMIN.PARTNERS}
          element={
            <Suspense fallback={<FullPageLoading />}>
              <SuperAdminPartners />
            </Suspense>
          }
        />
        <Route
          path={APP_ROUTES.SUPER_ADMIN.COMPANIES}
          element={
            <Suspense fallback={<FullPageLoading />}>
              <SuperAdminCompanies />
            </Suspense>
          }
        />
        <Route
          path={APP_ROUTES.SUPER_ADMIN.USERS}
          element={
            <Suspense fallback={<FullPageLoading />}>
              <SuperAdminUsers />
            </Suspense>
          }
        />
        <Route
          path={APP_ROUTES.SUPER_ADMIN.APP_CONFIG}
          element={
            <Suspense fallback={<FullPageLoading />}>
              <SuperAdminAppConfig />
            </Suspense>
          }
        />
        <Route
          path={APP_ROUTES.SUPER_ADMIN.CLIENTS}
          element={
            <Suspense fallback={<FullPageLoading />}>
              <SuperAdminClients />
            </Suspense>
          }
        />
        <Route
          path={APP_ROUTES.SUPER_ADMIN.OPERATIONS}
          element={
            <Suspense fallback={<FullPageLoading />}>
              <SuperAdminOperations />
            </Suspense>
          }
        />
      </Route>

      {/* Reports */}
      <Route
        path={APP_ROUTES.REPORTS.MAIN}
        element={
          <Suspense fallback={<FullPageLoading />}>
            <EnforceAuth>
              <Reports />
            </EnforceAuth>
          </Suspense>
        }
      />

      {/* Meetings - Redirect to Reports with meetings tab for backward compatibility */}
      <Route
        path={APP_ROUTES.MEETINGS.MAIN}
        element={<Navigate to={APP_ROUTES.REPORTS.MEETINGS} replace />}
      />
      <Route
        path={APP_ROUTES.MEETINGS.SLUG}
        element={
          <Suspense fallback={<FullPageLoading />}>
            <MeetingReportWrapper />
          </Suspense>
        }
      />

      {/* Sessions */}
      <Route
        path={APP_ROUTES.SESSIONS.MAIN}
        element={<Navigate to={APP_ROUTES.REPORTS.SESSIONS} replace />}
      />
      <Route
        path={APP_ROUTES.SESSIONS.SLUG}
        element={
          <Suspense fallback={<FullPageLoading />}>
            <SessionReportWrapper />
          </Suspense>
        }
      />

      <Route
        path={APP_ROUTES.INVALID_CLIENT}
        element={
          <Suspense fallback={<FullPageLoading />}>
            <InvalidClient />
          </Suspense>
        }
      />

      <Route
        path={APP_ROUTES.OAUTH_CALLBACK.ZOOM}
        element={
          <Suspense fallback={<FullPageLoading />}>
            <EnforceAuth>
              <ZoomOAuthCallback />
            </EnforceAuth>
          </Suspense>
        }
      />
      <Route
        path={APP_ROUTES.OAUTH_CALLBACK.GOOGLE}
        element={
          <Suspense fallback={<FullPageLoading />}>
            <EnforceAuth>
              <GoogleCalendarOAuthCallback />
            </EnforceAuth>
          </Suspense>
        }
      />
      <Route
        path={APP_ROUTES.OAUTH_CALLBACK.MICROSOFT}
        element={
          <Suspense fallback={<FullPageLoading />}>
            <EnforceAuth>
              <MicrosoftCalendarOAuthCallback />
            </EnforceAuth>
          </Suspense>
        }
      />

      {/* Analytics Routes */}
      <Route
        path={APP_ROUTES.ANALYTICS.MY}
        element={
          <Suspense fallback={<FullPageLoading />}>
            <EnforceAuth>
              <MyAnalytics />
            </EnforceAuth>
          </Suspense>
        }
      />
      <Route
        path={APP_ROUTES.ANALYTICS.TEAM}
        element={
          <Suspense fallback={<FullPageLoading />}>
            <RoleBasedRoute
              allowedRoles={[
                UserRole.COMPANY_ADMIN,
                UserRole.COMPANY_MANAGER,
                UserRole.CHANNEL_PARTNER_ADMIN,
              ]}
            >
              <TeamAnalytics />
            </RoleBasedRoute>
          </Suspense>
        }
      />
      <Route
        path={APP_ROUTES.ANALYTICS.COMPANY}
        element={
          <Suspense fallback={<FullPageLoading />}>
            <RoleBasedRoute
              allowedRoles={[UserRole.COMPANY_ADMIN, UserRole.COMPANY_MANAGER]}
            >
              <Analytics />
            </RoleBasedRoute>
          </Suspense>
        }
      />
      <Route
        path={APP_ROUTES.ANALYTICS.PARTNER}
        element={
          <Suspense fallback={<FullPageLoading />}>
            <RoleBasedRoute allowedRoles={[UserRole.CHANNEL_PARTNER_ADMIN]}>
              <PartnerAnalytics />
            </RoleBasedRoute>
          </Suspense>
        }
      />
      <Route
        path={APP_ROUTES.ANALYTICS.PARTNER_COMPANY}
        element={
          <Suspense fallback={<FullPageLoading />}>
            <RoleBasedRoute allowedRoles={[UserRole.CHANNEL_PARTNER_ADMIN]}>
              <PartnerCompanyAnalytics />
            </RoleBasedRoute>
          </Suspense>
        }
      />
      <Route
        path='*'
        element={
          <Suspense fallback={<FullPageLoading />}>
            <NotFound />
          </Suspense>
        }
      />
    </SentryRoutes>
  );
};

export default ApplicationRoutes;
